import {
  NODE_RAW_MAINTENANCE_STATE,
  NODE_RAW_MAINTENANCE_TASK_STATE,
  NODES_STATUSES_V3,
  NODES_STATUSES_V3_TYPE
} from '@/common/constants/nodes.constants';
import {
  CS,
  MDS,
  NODE_SERVICE_STATUS
} from '@/common/constants/services.constants';
import {
  NodeChunkServiceInfo,
  NodeComputeServiceInfo,
  NodeMaintenanceState,
  NodeMasterServiceInfo,
  NodeServiceInfo,
  NodeStatus,
  NodeV3
} from '@schema';

type NodeServiceInfoAnyType = NodeServiceInfo|NodeMasterServiceInfo|NodeComputeServiceInfo|NodeChunkServiceInfo;

export function determineMaintenanceStatus(node: NodeV3): NODES_STATUSES_V3_TYPE|undefined {
  if (!node.maintenance) {
    return undefined;
  }

  const { state, task } = node.maintenance;

  // NOTE (alkurbatov): Tricky behaviour:
  // idle - reported when we did maintenance precheck, but haven't started maintenance.
  // completed - reported when we entered into maintenance and successfully exited from it.
  //
  // Both states mean that no maintenance in progress now.
  if (state === NODE_RAW_MAINTENANCE_STATE.IDLE || state === NODE_RAW_MAINTENANCE_STATE.COMPLETED) {
    return undefined;
  }

  if (!task) {
    return NODES_STATUSES_V3.MAINTENANCE;
  }

  if (state === NODE_RAW_MAINTENANCE_STATE.SUSPENDING) {
    if (task.state === NODE_RAW_MAINTENANCE_TASK_STATE.FAILED) {
      return NODES_STATUSES_V3.MAINTENANCE_SUSPENDING_INCOMPLETE;
    }

    return NODES_STATUSES_V3.MAINTENANCE_SUSPENDING;
  }

  if (state === NODE_RAW_MAINTENANCE_STATE.RESUMING) {
    if (task.state === NODE_RAW_MAINTENANCE_TASK_STATE.FAILED) {
      return NODES_STATUSES_V3.MAINTENANCE_RESUMING_INCOMPLETE;
    }

    return NODES_STATUSES_V3.MAINTENANCE_RESUMING;
  }

  return NODES_STATUSES_V3.MAINTENANCE;
}

function hasFailedCriticalServices(node: NodeV3): boolean {
  const csService = node.services[CS]?.f;
  const mdsService = node.services[MDS]?.f;
  return !!(csService || mdsService);
}

function getNodeServices(node: NodeV3): Array<NodeServiceInfoAnyType> {
  const initialServices = node.services || {};
  return Object.values(initialServices).filter(Boolean);
}

export function isNodeInProgress(node: NodeV3): boolean {
  const maintenanceStatus = determineMaintenanceStatus(node);
  const nodeServices = getNodeServices(node);
  const isServiceInProgress = !!nodeServices.find(({ b }) => b);
  const isNodeInRecovering = node?.recovery?.status === NodeV3.status.RECOVERING;
  const recoveryFailed = node?.recovery?.status === NodeV3.status.FAILED;

  // TODO: get types from the schema
  const inProgressStatuses: NodeStatus[] = [NodeStatus.INSTALLING, NodeStatus.RELEASING];
  const isNodeInProgressStatus = inProgressStatuses.some(nodeStatus => node.status.includes(nodeStatus));

  return isNodeInProgressStatus
    || isServiceInProgress
    || maintenanceStatus === NODES_STATUSES_V3.MAINTENANCE_RESUMING
    || maintenanceStatus === NODES_STATUSES_V3.MAINTENANCE_SUSPENDING
    || isNodeInRecovering
    || (node.tasks?.length > 0 && !recoveryFailed);
}

export function defineStatus(node: NodeV3): NODES_STATUSES_V3_TYPE {
  const maintenanceStatus = determineMaintenanceStatus(node);
  const nodeServices = getNodeServices(node);

  if (node?.recovery?.status === NodeV3.status.FAILED) {
    return NODES_STATUSES_V3.RECOVERY_FAILED;
  } else if (node?.recovery?.status === NodeV3.status.RECOVERING) {
    return NODES_STATUSES_V3.RECOVERING;
  } else if (node.status.includes(NodeStatus.INSTALLING)) {
    return NODES_STATUSES_V3.INSTALLING;
  } else if (maintenanceStatus) {
    return maintenanceStatus;
  } else if (node.status.includes(NodeStatus.RELEASING)) {
    return NODES_STATUSES_V3.RELEASING;
  } else if (isNodeInProgress(node)) {
    return NODES_STATUSES_V3.IN_PROGRESS;
  } else if (node.status.includes(NodeStatus.UNASSIGNED)) {
    return NODES_STATUSES_V3.UNASSIGNED;
  } else if (nodeServices.find(({ f }) => f)) {
    if (hasFailedCriticalServices(node)) {
      return NODES_STATUSES_V3.FAILED;
    }
    return NODES_STATUSES_V3.WARNING;
  }
  return NODES_STATUSES_V3.HEALTHY;
}

export function getServiceState(service: NodeServiceInfoAnyType, maintenance: NodeMaintenanceState): NODE_SERVICE_STATUS {
  if (maintenance) return NODE_SERVICE_STATUS.MAINTENANCE;
  if (service.f) return NODE_SERVICE_STATUS.FAILED;
  if ('ill' in service && service.ill) return NODE_SERVICE_STATUS.SLOW;
  return NODE_SERVICE_STATUS.HEALTHY;
}
